

import { DashboardLayout } from "@/components/dashboard/dashboard-layout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useState } from "react"
import { useCreateCreditTopupQR } from "@/src/hooks/useAddCredit"
import { Card } from "@/components/ui/card"
import { AlertCircle, CheckCircle, Loader2, Copy, Zap, Banknote, CreditCard, Smartphone, Wallet, QrCode } from "lucide-react"
import Image from "@/src/components/Image"
import { toast } from "sonner"
import { useNavigate } from "react-router-dom"
import { useAuth } from "@/src/contexts/AuthContext"
import { PAYMENT_FLAGS, SHOW_PAYMENT_SELECTOR, getDefaultPaymentMethod } from '@/lib/payment-flags'

interface BankTransferInfo {
  bankName: string
  accountNumber: string
  accountName: string
  amount: number
  referenceNumber: string
  instructions: string
}

export default function AddCreditPage() {
  const navigate = useNavigate()
  const { refreshCredits } = useAuth()
  const [amount, setAmount] = useState("")
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null)
  const [bankTransfer, setBankTransfer] = useState<BankTransferInfo | null>(null)
  const [creditsToAdd, setCreditsToAdd] = useState<number>(0)
  const [overageRate, setOverageRate] = useState<number>(1.0)
  const [step, setStep] = useState<'input' | 'instructions' | 'confirmation'>('input')
  const [paymentMethod, setPaymentMethod] = useState<'bank_transfer' | 'reservepay' | 'card' | 'mobile_banking' | 'true_money' | 'alipay' | 'wechat'>(getDefaultPaymentMethod())

  const quickAmounts = [100, 300, 500, 1000, 3000, 5000, 10000]

  const createPaymentMutation = useCreateCreditTopupQR()

  // Pre-flight check: verify credit purchase is allowed for current package
  const checkCreditPurchaseCap = async (amountTHB: number): Promise<{ allowed: boolean; error?: string }> => {
    try {
      const token = localStorage.getItem('authToken')
      if (!token) return { allowed: false, error: 'กรุณาเข้าสู่ระบบใหม่' }

      const API_BASE = import.meta.env.VITE_API_BASE_URL || ''
      const response = await fetch(`${API_BASE}/api/payment/user/credit/check-cap`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ amount: amountTHB })
      })

      const data = await response.json()
      if (!response.ok || !data.success) {
        return { allowed: false, error: data.error || 'ไม่สามารถตรวจสอบสิทธิ์ได้' }
      }
      return { allowed: true }
    } catch (err: any) {
      // If check fails, allow user to proceed (cap will be checked by backend on actual call)
      console.warn('Cap check failed, allowing proceed:', err.message)
      return { allowed: true }
    }
  }

  const handleQuickAmount = (value: number) => {
    setAmount(value.toString())
    setSelectedAmount(value)
  }

  const handleConfirm = async () => {
    const amountNum = parseFloat(amount)

    if (!amount || isNaN(amountNum)) {
      toast.error('กรุณากรอกจำนวนเงิน')
      return
    }

    if (amountNum < 100) {
      toast.error('จำนวนเงินต่ำสุดคือ 100 บาท')
      return
    }

    if (amountNum > 1000000) {
      toast.error('จำนวนเงินสูงสุดคือ 1,000,000 บาท')
      return
    }

    // For all ReservePay methods, check cap first
    if (['reservepay', 'card', 'mobile_banking', 'true_money', 'alipay', 'wechat'].includes(paymentMethod)) {
      const capCheck = await checkCreditPurchaseCap(amountNum)
      if (!capCheck.allowed) {
        toast.error(capCheck.error || 'ไม่สามารถซื้อเครดิตได้')
        return
      }
    }

    if (paymentMethod === 'reservepay') {
      // Navigate to full-page ReservePay flow — PromptPay
      navigate(`/payment/reservepay?amount=${amountNum}&type=credit`)
      return
    }

    if (paymentMethod === 'card') {
      // Navigate to full-page ReservePay flow — CARD
      navigate(`/payment/reservepay?amount=${amountNum}&type=credit&method=card`)
      return
    }

    if (paymentMethod === 'mobile_banking') {
      // Navigate to full-page ReservePay flow — Mobile Banking
      navigate(`/payment/reservepay?amount=${amountNum}&type=credit&method=mobile_banking`)
      return
    }

    if (paymentMethod === 'true_money') {
      // Navigate to full-page ReservePay flow — TrueMoney
      navigate(`/payment/reservepay?amount=${amountNum}&type=credit&method=true_money`)
      return
    }

    if (paymentMethod === 'alipay') {
      navigate(`/payment/reservepay?amount=${amountNum}&type=credit&method=alipay`)
      return
    }

    if (paymentMethod === 'wechat') {
      navigate(`/payment/reservepay?amount=${amountNum}&type=credit&method=wechat`)
      return
    }

    await createPaymentMutation.mutateAsync(
      { amount: amountNum },
      {
        onSuccess: (data) => {
          setSelectedAmount(amountNum)
          setBankTransfer(data.data.bankTransfer)
          setCreditsToAdd(data.data.creditsToAdd)
          setOverageRate(data.data.overageRate || 1.0)
          setStep('instructions')
          toast.success('สร้างรายการชำระเงินสำเร็จ')
        },
        onError: (error) => {
          toast.error(`เกิดข้อผิดพลาด: ${error.message}`)
        }
      }
    )
  }

  const isLoading = createPaymentMutation.isPending
  const confirmButtonLabelByMethod: Record<typeof paymentMethod, string> = {
    reservepay: 'ชำระผ่าน PromptPay',
    card: 'ชำระผ่านบัตร',
    mobile_banking: 'ชำระผ่าน Mobile Banking',
    true_money: 'ชำระผ่าน TrueMoney',
    alipay: 'ชำระผ่าน Alipay',
    wechat: 'ชำระผ่าน WeChat Pay',
    bank_transfer: 'ดูรายละเอียดการโอนเงิน'
  }

  if (step === 'instructions') {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <h1 className="text-2xl font-bold">เติมเครดิต</h1>

          <div className="max-w-3xl mx-auto space-y-6">
            <Card className="p-8">
              <div className="space-y-6">
                <div className="text-center space-y-2">
                  <h2 className="text-2xl font-bold text-[#7FB069]">
                    รายละเอียดการโอนเงิน
                  </h2>
                  <p className="text-gray-600">
                    โอนเงินตามบัญชีด้านล่างและส่งสลิปให้เจ้าหน้าที่
                  </p>
                </div>

                {/* Bank Account Details */}
                {bankTransfer && (
                  <div className="p-5 bg-green-50 rounded-xl border-2 border-green-300 space-y-3">
                    <p className="font-semibold text-green-900 text-lg">🏦 บัญชีปลายทาง</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">ธนาคาร</span>
                        <span className="font-semibold text-gray-900">{bankTransfer.bankName}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">เลขที่บัญชี</span>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-900 text-base tracking-wider">{bankTransfer.accountNumber}</span>
                          <button
                            onClick={() => { navigator.clipboard.writeText(bankTransfer.accountNumber); toast.success('คัดลอกเลขบัญชีแล้ว') }}
                            className="text-[#7FB069] hover:text-[#7FB069]/70 transition-colors"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">ชื่อบัญชี</span>
                        <span className="font-semibold text-gray-900">{bankTransfer.accountName}</span>
                      </div>
                      <div className="border-t border-green-200 pt-2 flex justify-between">
                        <span className="text-gray-600">จำนวนเงินที่ต้องโอน</span>
                        <span className="font-bold text-green-700 text-lg">฿{bankTransfer.amount.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Reference Number */}
                {bankTransfer && (
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-300 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-amber-800 font-medium">เลขอ้างอิง (ระบุในช่องหมายเหตุ)</p>
                      <p className="font-bold text-amber-900 text-lg tracking-widest">{bankTransfer.referenceNumber}</p>
                    </div>
                    <button
                      onClick={() => { navigator.clipboard.writeText(bankTransfer.referenceNumber); toast.success('คัดลอกเลขอ้างอิงแล้ว') }}
                      className="text-amber-700 hover:text-amber-900 transition-colors ml-4"
                    >
                      <Copy className="w-5 h-5" />
                    </button>
                  </div>
                )}

                {/* Credit summary */}
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-1 text-sm">
                  <p className="font-semibold text-blue-900">📊 สรุปรายการ</p>
                  <div className="flex justify-between text-blue-800">
                    <span>จำนวนเงิน</span>
                    <span className="font-semibold">฿{selectedAmount?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-blue-800">
                    <span>เครดิตที่จะได้รับ</span>
                    <span className="font-semibold">{creditsToAdd.toLocaleString()} เครดิต</span>
                  </div>
                  <div className="flex justify-between text-blue-800">
                    <span>อัตรา</span>
                    <span className="font-semibold">1 บาท = {(1 / overageRate).toFixed(3)} เครดิต</span>
                  </div>
                </div>

                {/* Steps */}
                <div className="space-y-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-amber-900">📱 ขั้นตอนการชำระเงิน:</p>
                      <ol className="space-y-1 text-sm text-amber-800 mt-2 list-decimal list-inside">
                        <li>โอนเงินตามจำนวนและบัญชีที่ระบุข้างบน</li>
                        <li>ระบุเลขอ้างอิงในช่องหมายเหตุการโอนเงิน</li>
                        <li>ถ่ายภาพหรือ screenshot สลิปการโอนเงิน</li>
                        <li>ส่งสลิปเข้า LINE Official Account <span className="font-semibold">@thaislipcheck</span></li>
                        <li>รอเจ้าหน้าที่อนุมัติ (โดยปกติ 1–2 ชั่วโมง)</li>
                      </ol>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setStep('input')
                      setBankTransfer(null)
                      setAmount('')
                      setSelectedAmount(null)
                    }}
                    className="flex-1"
                  >
                    ยกเลิก
                  </Button>
                  <Button
                    onClick={() => {
                      setStep('confirmation')
                      toast.success('บันทึกรายการแล้ว รอเจ้าหน้าที่ตรวจสอบสลิป')
                      refreshCredits()
                    }}
                    className="flex-1 bg-[#7FB069] hover:bg-[#7FB069]/90"
                  >
                    ✅ โอนเงินแล้ว
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  if (step === 'confirmation') {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <h1 className="text-2xl font-bold">เติมเครดิต</h1>

          <div className="max-w-3xl mx-auto">
            <Card className="p-8">
              <div className="text-center space-y-6">
                <div className="flex justify-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-green-600">แจ้งชำระเงินสำเร็จ!</h2>
                  <p className="text-gray-600 mt-2">
                    กรุณาส่งสลิปการโอนเงินให้เจ้าหน้าที่ เครดิตจะถูกเติมหลังได้รับการอนุมัติ
                  </p>
                </div>

                <div className="space-y-3 p-4 bg-green-50 rounded-lg border border-green-200">
                  <div>
                    <p className="text-sm text-gray-600">จำนวนเงินที่โอน</p>
                    <p className="text-2xl font-bold text-green-600">
                      {selectedAmount?.toLocaleString()} บาท
                    </p>
                  </div>
                  <div className="border-t border-green-200 pt-3">
                    <p className="text-sm text-gray-600">เครดิตที่จะได้รับ (หลังอนุมัติ)</p>
                    <p className="text-2xl font-bold text-green-600">
                      +{creditsToAdd.toLocaleString()} เครดิต
                    </p>
                  </div>
                </div>

                <Button
                  onClick={() => navigate('/dashboard')}
                  className="w-full bg-[#7FB069] hover:bg-[#7FB069]/90"
                >
                  กลับไปที่แดชบอร์ด
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">เติมเครดิต</h1>

        <div className="">
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="space-y-4">
              {/* Payment method selector — shown only when both methods are enabled */}
              {SHOW_PAYMENT_SELECTOR && (
              <div>
                <p className="font-semibold mb-3">วิธีการชำระเงิน</p>
                <div className={`grid gap-3 ${
                  (PAYMENT_FLAGS.reservepay ? 1 : 0) + (PAYMENT_FLAGS.bankTransfer ? 1 : 0) + (PAYMENT_FLAGS.card ? 1 : 0) + (PAYMENT_FLAGS.mobileBanking ? 1 : 0) >= 4
                    ? 'grid-cols-3'
                    : (PAYMENT_FLAGS.reservepay ? 1 : 0) + (PAYMENT_FLAGS.bankTransfer ? 1 : 0) + (PAYMENT_FLAGS.card ? 1 : 0) + (PAYMENT_FLAGS.mobileBanking ? 1 : 0) >= 3
                    ? 'grid-cols-3'
                    : PAYMENT_FLAGS.reservepay && PAYMENT_FLAGS.bankTransfer ? 'grid-cols-2' : 'grid-cols-1'
                }`}>
                  {PAYMENT_FLAGS.reservepay && (
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('reservepay')}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                      paymentMethod === 'reservepay'
                        ? 'border-[#7FB069] bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Zap className={`w-6 h-6 ${paymentMethod === 'reservepay' ? 'text-[#7FB069]' : 'text-gray-400'}`} />
                    <span className={`font-semibold text-sm ${paymentMethod === 'reservepay' ? 'text-[#7FB069]' : 'text-gray-600'}`}>
                      PromptPay อัตโนมัติ
                    </span>
                    <span className="text-xs text-gray-400 text-center">สแกน QR จ่ายทันที ยืนยันอัตโนมัติ</span>
                    {paymentMethod === 'reservepay' && (
                      <span className="text-xs bg-[#7FB069] text-white px-2 py-0.5 rounded-full">แนะนำ</span>
                    )}
                  </button>
                  )}
                  {PAYMENT_FLAGS.bankTransfer && (
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('bank_transfer')}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                      paymentMethod === 'bank_transfer'
                        ? 'border-blue-400 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Banknote className={`w-6 h-6 ${paymentMethod === 'bank_transfer' ? 'text-blue-500' : 'text-gray-400'}`} />
                    <span className={`font-semibold text-sm ${paymentMethod === 'bank_transfer' ? 'text-blue-600' : 'text-gray-600'}`}>
                      โอนเงินผ่านธนาคาร
                    </span>
                    <span className="text-xs text-gray-400 text-center">โอนแล้วส่งสลิปให้เจ้าหน้าที่</span>
                  </button>
                  )}
                  {PAYMENT_FLAGS.card && (
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('card')}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                      paymentMethod === 'card'
                        ? 'border-purple-400 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <CreditCard className={`w-6 h-6 ${paymentMethod === 'card' ? 'text-purple-500' : 'text-gray-400'}`} />
                    <span className={`font-semibold text-sm ${paymentMethod === 'card' ? 'text-purple-600' : 'text-gray-600'}`}>
                      บัตรเครดิต/เดบิต
                    </span>
                    <span className="text-xs text-gray-400 text-center">ชำระผ่านบัตร ปลอดภัย 3D Secure</span>
                  </button>
                  )}
                  {PAYMENT_FLAGS.mobileBanking && (
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('mobile_banking')}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                      paymentMethod === 'mobile_banking'
                        ? 'border-orange-400 bg-orange-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Smartphone className={`w-6 h-6 ${paymentMethod === 'mobile_banking' ? 'text-orange-500' : 'text-gray-400'}`} />
                    <span className={`font-semibold text-sm ${paymentMethod === 'mobile_banking' ? 'text-orange-600' : 'text-gray-600'}`}>
                      Mobile Banking
                    </span>
                    <span className="text-xs text-gray-400 text-center">ชำระผ่านแอปธนาคาร BBL / TTB</span>
                  </button>
                  )}
                  {PAYMENT_FLAGS.trueMoney && (
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('true_money')}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                      paymentMethod === 'true_money'
                        ? 'border-red-400 bg-red-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Wallet className={`w-6 h-6 ${paymentMethod === 'true_money' ? 'text-red-500' : 'text-gray-400'}`} />
                    <span className={`font-semibold text-sm ${paymentMethod === 'true_money' ? 'text-red-600' : 'text-gray-600'}`}>
                      TrueMoney Wallet
                    </span>
                    <span className="text-xs text-gray-400 text-center">ชำระผ่าน TrueMoney Wallet</span>
                  </button>
                  )}
                  {PAYMENT_FLAGS.alipay && (
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('alipay')}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                      paymentMethod === 'alipay'
                        ? 'border-blue-400 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <QrCode className={`w-6 h-6 ${paymentMethod === 'alipay' ? 'text-blue-500' : 'text-gray-400'}`} />
                    <span className={`font-semibold text-sm ${paymentMethod === 'alipay' ? 'text-blue-600' : 'text-gray-600'}`}>
                      Alipay
                    </span>
                    <span className="text-xs text-gray-400 text-center">สแกน QR จ่ายผ่าน Alipay</span>
                  </button>
                  )}
                  {PAYMENT_FLAGS.wechat && (
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('wechat')}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                      paymentMethod === 'wechat'
                        ? 'border-emerald-400 bg-emerald-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <QrCode className={`w-6 h-6 ${paymentMethod === 'wechat' ? 'text-emerald-500' : 'text-gray-400'}`} />
                    <span className={`font-semibold text-sm ${paymentMethod === 'wechat' ? 'text-emerald-600' : 'text-gray-600'}`}>
                      WeChat Pay
                    </span>
                    <span className="text-xs text-gray-400 text-center">สแกน QR จ่ายผ่าน WeChat</span>
                  </button>
                  )}
                </div>
              </div>
              )}

              <div className="flex items-center gap-2">
                <Image
                  src="/icons/icon-money.svg"
                  alt="Money"
                  width={24}
                  height={24}
                  className="brightness-0"
                />
                <span className="font-semibold">จำนวนเงิน (บาท)</span>
              </div>

              <Input
                type="number"
                placeholder="กรอกจำนวนเงิน"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="text-lg"
                disabled={isLoading}
              />

              <div className="flex flex-wrap gap-3 justify-center">
                {quickAmounts.map((value) => (
                  <Button
                    key={value}
                    variant={selectedAmount === value ? "default" : "outline"}
                    onClick={() => handleQuickAmount(value)}
                    disabled={isLoading}
                    className={
                      selectedAmount === value
                        ? "bg-[#6FBADB] text-white min-w-[80px]"
                        : "border-[#6FBADB] text-[#6FBADB] hover:bg-[#6FBADB] hover:text-white min-w-[80px]"
                    }
                  >
                    {value.toLocaleString()}
                  </Button>
                ))}
              </div>

              <div className="flex justify-center">
                <Button
                  onClick={handleConfirm}
                  disabled={isLoading || !amount}
                  className="bg-[#E07A5F] hover:bg-[#E07A5F]/90 text-white px-8"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      กำลังสร้างรายการ...
                    </>
                  ) : (
                    confirmButtonLabelByMethod[paymentMethod]
                  )}
                </Button>
              </div>

              <div className="text-center space-y-1 text-gray-600">
                <p>ระบบจะแสดงรายละเอียดบัญชีธนาคารให้โอนเงิน</p>
                <p>พร้อมเลขอ้างอิงสำหรับระบุในช่องหมายเหตุ</p>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-300" />

        {/* Important Information Box */}
        <div className="">
          <div className="max-w-3xl mx-auto">
            <div className="p-4 bg-red-50 border-2 border-red-300 rounded-lg">
              <div className="flex gap-3">
                <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-800 font-semibold">
                    หากพบปัญหา กรุณาติดต่อ Line OA <span className="font-bold">@Thaislipcheck</span> เพื่อให้เจ้าหน้าที่ตรวจสอบ
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
